import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
export function InstitutionalPartners() {
  return <>
      <SubpageHero eyebrow="Strategic collaboration for scalable impact" title="Institutional Partners" paragraphs={['We collaborate with foundations, governmental bodies, NGOs, and educational institutions that recognize the importance of preserving Islamic identity in minority contexts, supporting family stability and child development, and promoting responsible participation in broader society.', 'Partnerships are multi-year, structured, and built on shared accountability.']} primaryCta={{
      label: 'Request Partner Brief',
      to: '/en/contact#general-inquiry'
    }} secondaryCta={{
      label: 'Schedule a Call',
      to: '/en/contact#general-inquiry'
    }} image="https://cdn.magicpatterns.com/uploads/tMN5qpJji3ZtLNNprM5Hpk/Institutional3.jpg" imageAlt="Institutional partners meeting around a boardroom table with an EU flag and IILH documents, Eiffel Tower in view" compact masked data-id="element-4815" data-mp="tbuzdlupiu2o" />

      {/* Partnership focus */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-4816" data-mp="1ylbnbn54vtj0">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4817" data-mp="1uhvruu4svz7w" />
        <Container data-id="element-4818" data-mp="1pk41i4jt0l5t">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4819" data-mp="1pknc1074k79t">
            Partnership focus
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4820" data-mp="j5mn3egiopcq">
            Institutional partners contribute to:
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-3" data-id="element-4821" data-mp="e71i71qblhhy">
            {["Children's Programs and identity-based education", 'Family education and parental support initiatives', 'Scholarships and equal-access programs', 'Research, advocacy, and community capacity-building'].map(item => <li key={item} className="flex items-start gap-3 text-ink-700" data-id="element-4822" data-mp="algb4wlu6dut">
                <CheckCircle2Icon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.8} data-id="element-4823" data-mp="3szz8r208p5r2" />
                {item}
              </li>)}
          </ul>
        </Container>
      </section>

      {/* What we offer */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4824" data-mp="3o2p96tj4zdpi">
        <Container data-id="element-4825" data-mp="3a2elfzd4if8k">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4826" data-mp="3a1wfgb0nsmng">
            What institutional partners receive
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4827" data-mp="3f3c3mby4v9a2">
            {[{
            title: 'Structured governance',
            body: 'Formal agreements, clear roles, and ethical oversight — aligned with international best practice.'
          }, {
            title: 'Impact reporting',
            body: 'Quarterly and annual reports covering programs, outcomes, finances, and safeguarding.'
          }, {
            title: 'Multi-year delivery',
            body: 'Scalable initiatives designed for continuity, not one-off projects.'
          }, {
            title: 'Joint visibility',
            body: "Co-branded outputs and recognition aligned with each partner's mission."
          }].map((card, i) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i]}`} data-id="element-4828" data-mp="ynjj53ekc99" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-4829" data-mp="17k176ixu3wnq">
                  {card.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-4830" data-mp="1gims2kqs3nro">
                  {card.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Ready to support CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4831" data-mp="2rx0ctgq8jn3h">
        <Container data-id="element-4832" data-mp="2ww3dkoz6n75n">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4833" data-mp="2wvl7l0mpxekj">
            <div className="max-w-xl" data-id="element-4834" data-mp="36zi5idajs7ab">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4835" data-mp="372nuztn39b6k">
                Ready to support?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4836" data-mp="2xa8gx7ewx6qy">
                Choose the most relevant option — donation, scholarships, or
                institutional partnership. We'll respond quickly with next
                steps.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4837" data-mp="2o7z7kkvdrjf5">
              <Link to="/en/contact#general-inquiry" data-id="element-4838" data-mp="2j9xmjuee9a11">
                <Button variant="primary" data-id="element-4839" data-mp="2jdboj1j5bh7y">Donate</Button>
              </Link>
              <Link to="/en/contact#general-inquiry" data-id="element-4840" data-mp="2ec5u8z9u7lwb">
                <Button variant="secondary" data-id="element-4841" data-mp="2ab2fkg3izvwo">Become a Partner</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}